import {
  type InstalledWidget,
  type NewInstalledWidget,
  type Widget,
  type WidgetData,
  type WidgetSizeDefinition,
  deleteWidgetPreferences,
  updateWidgetPreferences,
  useWidgetPreferences,
} from "@repo/api";
import { t } from "@repo/translation";
import { STORAGE_KEY, removeStorageItem } from "@repo/ui/src/storage/storage";
import { type Toast } from "@repo/utils";
import { useEffect, useState } from "react";

import {
  getNewCoordinates,
  moveWidget,
  moveWidgetToPosition as moveWidgetToPositionFn,
  resizeWidget,
} from "./reposition";

export const useInstalledWidgets = (updateToast: (toast: Toast) => void) => {
  const { data: widgetPreferences } = useWidgetPreferences();
  const [installedWidgets, setInstalledWidgets] = useState<WidgetData[]>(
    widgetPreferences ?? []
  );

  useEffect(() => {
    setInstalledWidgets(widgetPreferences ?? []);
  }, [widgetPreferences]);

  const addWidget = async (
    widget: Widget,
    size: WidgetSizeDefinition,
    settings: Record<string, object>
  ): Promise<InstalledWidget | undefined> => {
    const coordinates = getNewCoordinates(installedWidgets, size);

    const newWidget: NewInstalledWidget = {
      ...widget,
      widgetType: widget.widgetType,
      widgetSettings: {
        appSize: size,
        appSetting: settings,
        top: coordinates.top,
        left: coordinates.left,
      },
    };

    try {
      const res = await updateWidgetPreferences(newWidget).then((r) => {
        updateToast({
          id: r.workspacePreferenceId,
          title: t("prompt.workspace_updated"),
        });

        return r;
      });
      const newInstalledWidget = {
        ...newWidget,
        workspacePreferenceId: res.workspacePreferenceId,
      };

      const newInstalledWidgets: WidgetData[] = [
        ...installedWidgets,
        newInstalledWidget,
      ];
      setInstalledWidgets(newInstalledWidgets);

      return newInstalledWidget;
    } catch {
      updateToast({
        title: t("prompt.workspace_updated_failed"),
        type: "error",
      });
    }
  };

  const removeWidget = async (workspacePreferenceId: number) => {
    const newInstalledWidgets = installedWidgets.filter(
      (w) => w.workspacePreferenceId !== workspacePreferenceId
    );
    await deleteWidgetPreferences(workspacePreferenceId)
      .then((r) => {
        updateToast({
          id: workspacePreferenceId,
          title: t("prompt.workspace_updated"),
        });

        return r;
      })
      .catch(() => {
        updateToast({
          id: workspacePreferenceId,
          title: t("prompt.workspace_updated_failed"),
          type: "error",
        });
      });
    setInstalledWidgets(newInstalledWidgets);

    return newInstalledWidgets;
  };

  const updateWidget = async (widget: InstalledWidget) => {
    const installedWidget = installedWidgets.find(
      (w) => w.workspacePreferenceId === widget.workspacePreferenceId
    );

    if (installedWidget) {
      installedWidget.widgetSettings = {
        ...widget.widgetSettings,
      };
      installedWidget.widgetAliasName = widget.widgetAliasName;

      await updateWidgetPreferences(installedWidget)
        .then((r) => {
          updateToast({
            id: r.workspacePreferenceId,
            title: t("prompt.workspace_updated"),
          });

          return r;
        })
        .catch(() => {
          updateToast({
            id: installedWidget.workspacePreferenceId,
            title: t("prompt.workspace_updated_failed"),
            type: "error",
          });
        });
      setInstalledWidgets([...installedWidgets]);
    }
  };

  const reorderWidget = async (
    widgetId: number,
    direction: "LEFT" | "RIGHT" | "UP" | "DOWN"
  ) => {
    const { newWidgets, updatedWidgets } = moveWidget(
      installedWidgets,
      widgetId,
      direction
    );

    try {
      await Promise.all(
        updatedWidgets.map((widget) => updateWidgetPreferences(widget))
      );

      updateToast({
        id: updatedWidgets.map((w) => w.workspacePreferenceId).join(","),
        title: t("prompt.workspace_updated"),
      });
      setInstalledWidgets([...newWidgets]);
    } catch {
      updateToast({
        id: updatedWidgets.map((w) => w.workspacePreferenceId).join(","),
        title: t("prompt.workspace_updated_failed"),
        type: "error",
      });
    }
  };

  const moveWidgetToPosition = async (
    widgetId: number,
    newLeft: number,
    newTop: number
  ) => {
    const previousWidgets = installedWidgets.map((w) => ({
      ...w,
      widgetSettings: {
        ...w.widgetSettings,
        appSize: { ...w.widgetSettings.appSize },
      },
    }));
    const { newWidgets, updatedWidgets } = moveWidgetToPositionFn(
      installedWidgets,
      widgetId,
      newLeft,
      newTop
    );

    // Optimistic update: apply new positions immediately so there's no snap-back
    setInstalledWidgets([...newWidgets]);

    try {
      await Promise.all(
        updatedWidgets.map((widget) => updateWidgetPreferences(widget))
      );

      updateToast({
        id: updatedWidgets.map((w) => w.workspacePreferenceId).join(","),
        title: t("prompt.workspace_updated"),
      });
    } catch {
      // Revert to previous state on failure
      setInstalledWidgets(previousWidgets);
      updateToast({
        id: updatedWidgets.map((w) => w.workspacePreferenceId).join(","),
        title: t("prompt.workspace_updated_failed"),
        type: "error",
      });
    }
  };

  const updateWidgetSize = async (
    widgetId: number,
    size: WidgetSizeDefinition
  ) => {
    const updatedWidgets = resizeWidget(installedWidgets, widgetId, size);

    try {
      await Promise.all(
        updatedWidgets.map((widget) => updateWidgetPreferences(widget))
      );

      updateToast({
        id: updatedWidgets.map((w) => w.workspacePreferenceId).join(","),
        title: t("prompt.workspace_updated"),
      });
      setInstalledWidgets([...updatedWidgets]);
    } catch {
      updateToast({
        id: updatedWidgets.map((w) => w.workspacePreferenceId).join(","),
        title: t("prompt.workspace_updated_failed"),
        type: "error",
      });
    }
  };

  const resetWorkspace = async () => {
    try {
      await Promise.all(
        installedWidgets.map((widget) =>
          deleteWidgetPreferences(widget.workspacePreferenceId)
        )
      );
      setInstalledWidgets([]);
      removeStorageItem(STORAGE_KEY.LAST_APPLIED_TEMPLATE);
      updateToast({
        id: "reset-workspace",
        title: t("prompt.workspace_updated"),
      });
    } catch {
      updateToast({
        id: "reset-workspace",
        title: t("prompt.workspace_updated_failed"),
        type: "error",
      });
    }
  };

  return {
    installedWidgets,
    addWidget,
    updateWidget,
    removeWidget,
    reorderWidget,
    moveWidgetToPosition,
    updateWidgetSize,
    resetWorkspace,
  };
};
